import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Paper,
  Grid,
  Chip,
  Fade,
  Slide,
  Zoom,
} from '@mui/material';
import {
  Dashboard,
  ShoppingBag,
  RateReview,
  AdminPanelSettings,
  TrendingUp,
  People,
  Star,
  ArrowForward,
  Speed,
  Security,
  Palette,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const features = [
    {
      title: 'Умное управление продуктами',
      description: 'Современный каталог с интуитивным интерфейсом и быстрым поиском',
      icon: <ShoppingBag sx={{ fontSize: 48 }} />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      action: () => navigate('/products'),
      delay: 0,
    },
    {
      title: 'Интерактивные отзывы',
      description: 'Реальные отзывы с фотографиями и детальной модерацией',
      icon: <RateReview sx={{ fontSize: 48 }} />,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      action: () => navigate('/reviews'),
      delay: 200,
    },
    {
      title: 'Аналитика в реальном времени',
      description: 'Подробная статистика и тренды для принятия решений',
      icon: <TrendingUp sx={{ fontSize: 48 }} />,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      action: () => navigate('/products'),
      delay: 400,
    },
  ];

  const stats = [
    { label: 'Продуктов', value: '10+', icon: <ShoppingBag /> },
    { label: 'Отзывов', value: '31+', icon: <RateReview /> },
    { label: 'Пользователей', value: '10+', icon: <People /> },
    { label: 'Рейтинг', value: '4.8', icon: <Star /> },
  ];

  const benefits = [
    {
      icon: <Speed sx={{ fontSize: 32 }} />,
      title: 'Молниеносная скорость',
      description: 'Оптимизированная производительность для максимального комфорта'
    },
    {
      icon: <Security sx={{ fontSize: 32 }} />,
      title: 'Безопасность данных',
      description: 'Защита информации с использованием современных технологий'
    },
    {
      icon: <Palette sx={{ fontSize: 32 }} />,
      title: 'Современный дизайн',
      description: 'Интуитивный интерфейс в стиле лучших продуктов мира'
    },
  ];

  if (isAuthenticated && user?.role === 'admin') {
    features.push({
      title: 'Админ панель',
      description: 'Модерируйте отзывы и управляйте системой',
      icon: <AdminPanelSettings sx={{ fontSize: 48 }} />,
      gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
      action: () => navigate('/admin'),
      delay: 600,
    });
  }

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: '#f3fbfd',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={1000}>
            <Box textAlign="center" mb={8}>
              <Typography
                variant="h1"
                component="h1"
                gutterBottom
                sx={{
                  fontSize: { xs: '3rem', md: '4.5rem' },
                  fontWeight: 800,
                  color: '#1a1a1a',
                  mb: 3,
                }}
              >
                Система отзывов
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: '#666666',
                  fontWeight: 300,
                  mb: 4,
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                Современная платформа для управления продуктами и отзывами
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Chip
                  label="🚀 Быстро"
                  sx={{
                    background: 'rgba(0, 0, 0, 0.05)',
                    color: '#1a1a1a',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label="🔒 Безопасно"
                  sx={{
                    background: 'rgba(0, 0, 0, 0.05)',
                    color: '#1a1a1a',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label="💎 Современно"
                  sx={{
                    background: 'rgba(0, 0, 0, 0.05)',
                    color: '#1a1a1a',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
          </Fade>

          {/* Stats Section */}
          <Fade in timeout={1500}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 8, justifyContent: 'center' }}>
              {stats.map((stat, index) => (
                <Slide direction="up" in timeout={1500 + index * 200} key={index}>
                  <Card
                    sx={{
                      background: '#ffffff',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: 3,
                      p: 3,
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      flex: '1 1 200px',
                      maxWidth: '250px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <Box sx={{ color: '#1a1a1a', mb: 1 }}>
                      {stat.icon}
                    </Box>
                    <Typography variant="h4" sx={{ color: '#1a1a1a', fontWeight: 700, mb: 1 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666666' }}>
                      {stat.label}
                    </Typography>
                  </Card>
                </Slide>
              ))}
            </Box>
          </Fade>

          {/* Instructions Section */}
          <Fade in timeout={2000}>
            <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
              <Typography
                variant="h3"
                component="h2"
                textAlign="center"
                sx={{
                  fontWeight: 700,
                  mb: 6,
                  color: '#1a1a1a',
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                📚 Руководство по использованию системы
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {/* Модерация отзывов */}
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Card
                    sx={{
                      height: '100%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      borderRadius: 4,
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <CardContent sx={{ p: 4, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <AdminPanelSettings sx={{ fontSize: 40, mr: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          Модерация отзывов
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
                        Управляйте отзывами пользователей с помощью удобной админ-панели
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                        <li>Просматривайте новые отзывы в статусе "Ожидает модерации"</li>
                        <li>Одобряйте качественные отзывы кнопкой "Одобрить"</li>
                        <li>Отклоняйте спам и некачественные отзывы</li>
                        <li>Добавляйте комментарии модератора при необходимости</li>
                        <li>Просматривайте информацию о товаре и пользователе</li>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={() => navigate('/admin')}
                        sx={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.3)',
                          },
                        }}
                      >
                        Перейти в админку →
                      </Button>
                    </CardContent>
                  </Card>
                </Box>

                {/* Поиск и фильтрация */}
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Card
                    sx={{
                      height: '100%',
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white',
                      borderRadius: 4,
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(240, 147, 251, 0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <CardContent sx={{ p: 4, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <RateReview sx={{ fontSize: 40, mr: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          Поиск и фильтрация
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
                        Эффективно находите нужные отзывы с помощью мощных инструментов поиска
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                        <li>Фильтруйте отзывы по статусу (все, одобренные, ожидающие)</li>
                        <li>Используйте пагинацию для просмотра больших объемов</li>
                        <li>Сортируйте по дате создания или рейтингу</li>
                        <li>Просматривайте детальную информацию о каждом отзыве</li>
                        <li>Переходите к товару прямо из списка отзывов</li>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={() => navigate('/reviews')}
                        sx={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.3)',
                          },
                        }}
                      >
                        Просмотреть отзывы →
                      </Button>
                    </CardContent>
                  </Card>
                </Box>

                {/* Статистика и аналитика */}
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Card
                    sx={{
                      height: '100%',
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      color: 'white',
                      borderRadius: 4,
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(79, 172, 254, 0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <CardContent sx={{ p: 4, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <TrendingUp sx={{ fontSize: 40, mr: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          Статистика и аналитика
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
                        Отслеживайте ключевые метрики и анализируйте тренды
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                        <li>Общее количество отзывов и продуктов</li>
                        <li>Средний рейтинг по всем товарам</li>
                        <li>Распределение оценок (5 звезд, 4 звезды и т.д.)</li>
                        <li>Статистика модерации (одобрено/отклонено)</li>
                        <li>Активность пользователей</li>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={() => navigate('/products')}
                        sx={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.3)',
                          },
                        }}
                      >
                        Посмотреть статистику →
                      </Button>
                    </CardContent>
                  </Card>
                </Box>

                {/* Управление продуктами */}
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Card
                    sx={{
                      height: '100%',
                      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                      color: 'white',
                      borderRadius: 4,
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(255, 107, 107, 0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <CardContent sx={{ p: 4, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <ShoppingBag sx={{ fontSize: 40, mr: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          Управление продуктами
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
                        Создавайте и управляйте каталогом товаров
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                        <li>Добавляйте новые товары с описанием и ценой</li>
                        <li>Редактируйте информацию о существующих товарах</li>
                        <li>Просматривайте все отзывы по конкретному товару</li>
                        <li>Удаляйте товары при необходимости</li>
                        <li>Связывайте товары с внешними ID для интеграции</li>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={() => navigate('/products')}
                        sx={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.3)',
                          },
                        }}
                      >
                        Управлять товарами →
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
              </Box>

              {/* Quick Start Guide */}
              <Card
                sx={{
                  mt: 6,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                    🚀 Быстрый старт
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                          1. Создайте товар
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Добавьте товар в каталог с описанием и ценой
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                          2. Модерируйте отзывы
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Проверяйте и одобряйте отзывы пользователей
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                          3. Анализируйте данные
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Изучайте статистику и улучшайте сервис
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 12, background: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Fade in timeout={2500}>
            <Box textAlign="center" mb={8}>
              <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Почему выбирают нас?
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
                Мы создали продукт, который сочетает в себе красоту, функциональность и производительность
              </Typography>
            </Box>
          </Fade>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
            {benefits.map((benefit, index) => (
              <Slide direction="up" in timeout={2500 + index * 300} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    background: '#ffffff',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: 4,
                    p: 4,
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    flex: '1 1 300px',
                    maxWidth: '350px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '50%',
                      width: 64,
                      height: 64,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      color: 'white',
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Card>
              </Slide>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Welcome Message */}
      {isAuthenticated && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Container maxWidth="md">
            <Fade in timeout={3000}>
              <Card
                sx={{
                  background: '#ffffff',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: 4,
                  p: 6,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                  Добро пожаловать, {user?.username}! 👋
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                  Выберите раздел для работы с системой
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/products')}
                    sx={{
                      background: '#1a1a1a',
                      color: '#ffffff',
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        background: '#333333',
                      },
                    }}
                  >
                    Каталог продуктов
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/reviews')}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: '#1a1a1a',
                      color: '#1a1a1a',
                      '&:hover': {
                        borderColor: '#333333',
                        color: '#333333',
                        background: 'rgba(0, 0, 0, 0.05)',
                      },
                    }}
                  >
                    Отзывы
                  </Button>
                </Box>
              </Card>
            </Fade>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default HomePage;